import React from "react";
import { NativeStackScreenProps } from "@react-navigation/native-stack";
import { ActivityIndicator, View } from "react-native";
import { FieldValue } from "@atgof-firebase/firebase";
import DetailPage from "../components/pages/DetailPage";
import { LanguageContext } from "../common/language";
import { RootStackParamList } from "../types";
import { episodeLabel } from "../common/util";
import { toEpisode } from "../common/episode";
import SectionHeader from "../components/SectionHeader";
import NewSheetOrApplicandButton from "../components/NewSheetOrApplicandButton";
import { UserContext } from "../data/userContext";
import { useNavigation } from "@react-navigation/native";
import {
  BreakdownSheets,
  BreakdownSheetsForScenes,
} from "../components/BreakdownSheets";
import { ScriptButton } from "../components/ScriptButton";
import { ProjectContext } from "../data/projectContext";
import {
  SceneSheets,
  SheetCategory,
  sheetCategorySpecs,
} from "../common/sheet";
import { transformSnapshot, useSnapshot } from "../data/useSnapshot";
import _ from "lodash";
import {
  getEpisodeApplicandsRef,
  onEpisodeApplicandSheets,
  onEpisodeSceneSheets,
} from "../common/episodeBreakdown";
import { useDocumentReference } from "../data/firestore";
import { DocumentSnapshot } from "@atgof-firebase/types";
import { useUserInkColours } from "../data/useUserInkColours";
import { UserColoursKey } from "../components/UserColoursKey";
import { useProject } from "../data/useProject";
import { useDocument } from "../data/useDocument";

export default function EpisodeBreakdownScreen({
  route,
}: NativeStackScreenProps<RootStackParamList, "episodeBreakdown">) {
  const { ph } = React.useContext(LanguageContext);
  const { path, subjectKind, subjectName, performerKind } = route.params;
  const { categories } = React.useContext(ProjectContext);
  const { user } = React.useContext(UserContext);
  const { navigate } = useNavigation();
  const subject = React.useMemo(
    () => ({ subjectKind, subjectName, performerKind }),
    [subjectKind, subjectName, performerKind],
  );
  const sheetCategories = React.useMemo(
    () =>
      categories.filter(
        (category) =>
          category in sheetCategorySpecs &&
          "newLookPh" in sheetCategorySpecs[category as SheetCategory],
      ) as SheetCategory[],
    [categories],
  );
  const episodeRef = useDocumentReference(path);
  const episode = useSnapshot(
    transformSnapshot(_.bind(episodeRef.onSnapshot, episodeRef), toEpisode),
    [episodeRef],
  );
  const sceneSheets = useSnapshot<SceneSheets[] | undefined>(
    _.curry(onEpisodeSceneSheets)(episodeRef, sheetCategories, subject),
    [episodeRef, sheetCategories, subject],
  );
  const episodeLookSheets = useSnapshot<
    Record<SheetCategory, DocumentSnapshot[]> | undefined
  >(
    _.curry(onEpisodeApplicandSheets)(
      episodeRef,
      "look",
      sheetCategories,
      subject,
    ),
    [episodeRef, sheetCategories, subject],
  );
  const episodeLooksRef = React.useMemo(
    () => getEpisodeApplicandsRef(episodeRef, "look", subject),
    [episodeRef, subject],
  );
  const newEpisodeLookAction = React.useCallback(
    (category: SheetCategory) => {
      return () => {
        if (!episodeLooksRef) return;
        const newDoc = episodeLooksRef.doc();
        newDoc.set({
          createdBy: user.ref,
          createdAt: FieldValue.serverTimestamp(),
          lastModifiedBy: user.ref,
          lastModifiedAt: FieldValue.serverTimestamp(),
          order: episodeLookSheets ? episodeLookSheets[category].length : 0,
          deleted: false,
          scenes: [],
          category,
        });
        navigate("applicand", {
          isNew: "true",
          path: newDoc.path,
          subjectKind,
          subjectName,
          episodePath: path,
        });
      };
    },
    [episodeLooksRef, user.id, navigate, episodeLookSheets],
  );
  const inkColours = useUserInkColours();
  const project = useDocument(useProject());
  const daysRatherThanEpisodes: boolean =
    project?.get("daysRatherThanEpisodes") ?? false;

  return (
    <DetailPage
      title={`${subjectName} - ${episodeLabel(ph, path, false, daysRatherThanEpisodes)}`}
      headerRight={
        <ScriptButton
          wholeEpisode
          parent={episode}
          subjectKind={subjectKind}
          subjectName={subjectName}
        />
      }
    >
      <View>
        <UserColoursKey value={inkColours} />
        <SectionHeader
          title={
            ph(
              daysRatherThanEpisodes ? "story-day-looks" : "episode-looks",
            ) as string
          }
        >
          {!episodeLookSheets ? (
            <ActivityIndicator style={{ marginLeft: 8 }} />
          ) : (
            <NewSheetOrApplicandButton
              kind="episode"
              restrictToCategories={sheetCategories}
              actionForCategory={newEpisodeLookAction}
            />
          )}
        </SectionHeader>
        {Object.keys(episodeLookSheets ?? {}).map((category) => (
          <BreakdownSheets
            key={category}
            sheets={episodeLookSheets![category as SheetCategory]}
            subjectKind={subjectKind}
            subjectName={subjectName}
            episodePath={path}
            sheetCategories={sheetCategories}
            inkColours={inkColours}
            horizontal={
              _.get(project?.get("breakdownSettings"), [
                "look",
                category,
                "horizontal",
              ])
                ? true
                : false
            }
          />
        ))}
        <SectionHeader title={ph("scenes") as string} />
        <BreakdownSheetsForScenes
          sceneSheets={sceneSheets}
          subject={{ subjectKind, subjectName }}
          episodePath={path}
          sheetCategories={sheetCategories}
          inkColours={inkColours}
        />
      </View>
    </DetailPage>
  );
}
